<template>
  <header class="cyber-nav-bar">
    <!-- 霓虹扫描线 -->
    <div class="scan-line"></div>

    <div class="nav-content">
      <!-- 中央导航项 -->
      <div class="nav-items">
        <!-- 主页按钮 -->
        <button
          class="cyber-btn"
          :class="{ 'active': isHomePage }"
          @click="handleGoHome"
        >
          <span class="btn-icon">🏠</span>
          <span class="btn-text">{{ $t('nav.home') }}</span>
          <span class="btn-glow"></span>
        </button>

        <!-- 超强翻译工具按钮 -->
        <button
          class="cyber-btn attention-btn"
          :class="{ 'active': isTranslationToolPage }"
          @click="handleTranslationTool"
        >
          <span class="btn-icon">🔄</span>
          <span class="btn-text">{{ $t('nav.translationTool') }}</span>
          <span class="attention-badge">FLAGSHIP</span>
          <span class="btn-glow"></span>
        </button>

        <!-- 笔译服务按钮 -->
        <button
          class="cyber-btn service-btn"
          :class="{ 'active': isTranslationServicesPage }"
          @click="handleTranslationServices"
        >
          <span class="btn-icon">📄</span>
          <span class="btn-text">{{ $t('nav.translationServices') }}</span>
          <span class="btn-glow"></span>
        </button>

        <!-- API教程按钮 -->
        <button
          class="cyber-btn"
          :class="{ 'active': isApiTutorialPage }"
          @click="handleApiTutorial"
        >
          <span class="btn-icon">🔑</span>
          <span class="btn-text">{{ $t('nav.apiTutorial') }}</span>
          <span class="btn-glow"></span>
        </button>

        <!-- 其它工具下拉菜单 -->
        <div class="cyber-dropdown" :class="{ 'active': isToolsDropdownOpen, 'locked': toolsLocked }">
          <button
            class="cyber-btn dropdown-toggle"
            @click="toggleToolsDropdown"
            @mouseenter="openToolsDropdown"
            @mouseleave="closeToolsDropdown"
          >
            <span class="btn-icon">🛠️</span>
            <span class="btn-text">{{ $t('nav.otherTools') }}</span>
            <span class="dropdown-arrow">▼</span>
            <span class="btn-glow"></span>
            <span class="lock-indicator" v-if="toolsLocked">📌</span>
          </button>
          <div
            class="dropdown-menu tools-menu"
            @mouseenter="keepToolsDropdownOpen"
            @mouseleave="closeToolsDropdown"
          >
            <button
              class="dropdown-item"
              :class="{ 'active': isTerminologyToolPage }"
              @click="handleTerminologyTool"
            >
              <span class="item-icon">📚</span>
              <span class="item-text">{{ $t('nav.terminologyTool') }}</span>
            </button>
            <button
              class="dropdown-item"
              :class="{ 'active': isWordCountToolPage }"
              @click="handleWordCountTool"
            >
              <span class="item-icon">🔢</span>
              <span class="item-text">{{ $t('nav.wordCountTool') }}</span>
            </button>
            <button
              class="dropdown-item"
              :class="{ 'active': isWordFrequencyToolPage }"
              @click="handleWordFrequencyTool"
            >
              <span class="item-icon">🔍</span>
              <span class="item-text">{{ $t('nav.wordFrequencyTool') }}</span>
            </button>
            <button
              class="dropdown-item"
              :class="{ 'active': isCorpusAlignmentToolPage }"
              @click="handleCorpusAlignmentTool"
            >
              <span class="item-icon">🔗</span>
              <span class="item-text">{{ $t('nav.corpusAlignmentTool') }}</span>
            </button>
          </div>
        </div>

        <!-- 备考学习工具下拉菜单 -->
        <div class="cyber-dropdown" :class="{ 'active': isStudyDropdownOpen, 'locked': studyLocked }">
          <button
            class="cyber-btn dropdown-toggle primary"
            @click="toggleStudyDropdown"
            @mouseenter="openStudyDropdown"
            @mouseleave="closeStudyDropdown"
          >
            <span class="btn-icon">📚</span>
            <span class="btn-text">{{ $t('nav.studyTools') }}</span>
            <span class="dropdown-arrow">▼</span>
            <span class="btn-glow"></span>
            <span class="lock-indicator" v-if="studyLocked">📌</span>
          </button>
          <div
            class="dropdown-menu study-menu"
            @mouseenter="keepStudyDropdownOpen"
            @mouseleave="closeStudyDropdown"
          >
            <button
              class="dropdown-item is-disabled"
              aria-disabled="true"
              @click="handlePracticeUnavailable"
            >
              <span class="item-icon">📝</span>
              <span class="item-text">{{ $t('nav.cattiPractice') }}</span>
              <span class="item-lock" aria-hidden="true">🔒</span>
            </button>
            <button
              class="dropdown-item"
              @click="handleComprehensiveTest"
            >
              <span class="item-icon">📊</span>
              <span class="item-text">{{ $t('nav.cattiComprehensive') }}</span>
            </button>
          </div>
        </div>

      </div>

      <!-- 语言切换按钮 -->
      <div class="language-switcher">
        <button class="cyber-btn lang-btn" @click="toggleLanguage">
          <span class="btn-icon">🌍</span>
          <span class="btn-text">{{ currentLangText }}</span>
          <span class="btn-glow"></span>
        </button>
      </div>
    </div>

    <!-- 底部霓虹线 -->
    <div class="neon-line"></div>
  </header>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'

export default {
  name: 'CyberNavBar',
  components: {
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { locale, t } = useI18n()

    // 下拉菜单状态
    const isToolsDropdownOpen = ref(false)
    const isStudyDropdownOpen = ref(false)
    const toolsLocked = ref(false)
    const studyLocked = ref(false)
    let dropdownTimeout = null

    // 判断当前页面
    const isHomePage = computed(() => route.path === '/')
    const isTranslationToolPage = computed(() => route.path === '/translation-tool')
    const isTranslationServicesPage = computed(() => route.path === '/translation-services')
    const isApiTutorialPage = computed(() => route.path === '/api-tutorial')
    const isTerminologyToolPage = computed(() => route.path === '/terminology-tool')
    const isWordCountToolPage = computed(() => route.path === '/word-count-tool')
    const isWordFrequencyToolPage = computed(() => route.path === '/word-frequency-tool')
    const isCorpusAlignmentToolPage = computed(() => route.path === '/corpus-alignment-tool')

    // 工具下拉菜单控制
    const toggleToolsDropdown = () => {
      if (isToolsDropdownOpen.value) {
        // 如果当前已展开，点击收起
        isToolsDropdownOpen.value = false
      } else {
        // 如果当前未展开，点击展开并锁定
        isToolsDropdownOpen.value = true
        isStudyDropdownOpen.value = false
        toolsLocked.value = true
        studyLocked.value = false
      }
    }

    const openToolsDropdown = () => {
      // 只有在未锁定状态才响应鼠标悬停
      if (!toolsLocked.value && !studyLocked.value) {
        clearTimeout(dropdownTimeout)
        isToolsDropdownOpen.value = true
        isStudyDropdownOpen.value = false
      }
    }

    const closeToolsDropdown = () => {
      // 只有在未锁定状态才允许自动关闭
      if (!toolsLocked.value) {
        dropdownTimeout = setTimeout(() => {
          if (!toolsLocked.value) {
            isToolsDropdownOpen.value = false
          }
        }, 150)
      }
    }

    const keepToolsDropdownOpen = () => {
      clearTimeout(dropdownTimeout)
    }

    // 备考学习下拉菜单控制
    const toggleStudyDropdown = () => {
      if (isStudyDropdownOpen.value) {
        // 如果当前已展开，点击收起
        isStudyDropdownOpen.value = false
      } else {
        // 如果当前未展开，点击展开并锁定
        isStudyDropdownOpen.value = true
        isToolsDropdownOpen.value = false
        studyLocked.value = true
        toolsLocked.value = false
      }
    }

    const openStudyDropdown = () => {
      // 只有在未锁定状态才响应鼠标悬停
      if (!studyLocked.value && !toolsLocked.value) {
        clearTimeout(dropdownTimeout)
        isStudyDropdownOpen.value = true
        isToolsDropdownOpen.value = false
      }
    }

    const closeStudyDropdown = () => {
      // 只有在未锁定状态才允许自动关闭
      if (!studyLocked.value) {
        dropdownTimeout = setTimeout(() => {
          if (!studyLocked.value) {
            isStudyDropdownOpen.value = false
          }
        }, 150)
      }
    }

    const keepStudyDropdownOpen = () => {
      clearTimeout(dropdownTimeout)
    }

    // 解锁所有下拉菜单
    const unlockDropdowns = () => {
      toolsLocked.value = false
      studyLocked.value = false
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    // 点击外部关闭下拉菜单
    const handleClickOutside = (event) => {
      // 检查点击是否在下拉菜单外部
      const isClickOutsideDropdown = !event.target.closest('.cyber-dropdown')

      if (isClickOutsideDropdown) {
        unlockDropdowns()
      }
    }

    // 组件挂载时添加全局点击监听
    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
    })

    // 组件卸载时移除监听
    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    const handleGoHome = () => {
      router.push('/')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    // CATTI 实务系统暂未开放：入口置灰，点击只给提示、不跳转。
    // 解冻时恢复成：router.push('/practice')
    const handlePracticeUnavailable = () => {
      ElMessage.info(t('nav.featureNotOpen'))
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleComprehensiveTest = () => {
      router.push('/comprehensive-test')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleTranslationTool = () => {
      router.push('/translation-tool')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleTranslationServices = () => {
      router.push('/translation-services')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleApiTutorial = () => {
      router.push('/api-tutorial')
    }

    const handleTerminologyTool = () => {
      router.push('/terminology-tool')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleWordCountTool = () => {
      router.push('/word-count-tool')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleWordFrequencyTool = () => {
      router.push('/word-frequency-tool')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    const handleCorpusAlignmentTool = () => {
      router.push('/corpus-alignment-tool')
      isToolsDropdownOpen.value = false
      isStudyDropdownOpen.value = false
    }

    // 语言切换
    const currentLangText = computed(() => {
      return locale.value === 'zh-CN' ? 'CN' : 'EN'
    })

    const toggleLanguage = () => {
      const newLocale = locale.value === 'zh-CN' ? 'en-US' : 'zh-CN'
      locale.value = newLocale
      localStorage.setItem('locale', newLocale)
    }

    return {
      handleGoHome,
      handlePracticeUnavailable,
      handleComprehensiveTest,
      handleTranslationTool,
      handleTranslationServices,
      handleApiTutorial,
      handleTerminologyTool,
      handleWordCountTool,
      handleWordFrequencyTool,
      handleCorpusAlignmentTool,
      isHomePage,
      isTranslationToolPage,
      isTranslationServicesPage,
      isApiTutorialPage,
      isWordCountToolPage,
      isTerminologyToolPage,
      isWordFrequencyToolPage,
      isCorpusAlignmentToolPage,
      currentLangText,
      toggleLanguage,
      isToolsDropdownOpen,
      isStudyDropdownOpen,
      // 模板里 :class="{ locked: toolsLocked }" 和 v-if="studyLocked" 都用到了，
      // 之前漏在 return 外面，渲染时报 "Property ... is not defined on instance"
      toolsLocked,
      studyLocked,
      toggleToolsDropdown,
      openToolsDropdown,
      closeToolsDropdown,
      keepToolsDropdownOpen,
      toggleStudyDropdown,
      openStudyDropdown,
      closeStudyDropdown,
      keepStudyDropdownOpen,
      unlockDropdowns
    }
  }
}
</script>

<style scoped>
.cyber-nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: rgba(10, 14, 39, 0.85);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(138, 92, 246, 0.3);
  z-index: 1000;
}

/* 扫描线动画 */
.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(138, 92, 246, 0.8) 50%,
    transparent 100%);
  animation: scan 3s linear infinite;
}

@keyframes scan {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 底部霓虹线 */
.neon-line {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg,
    #8a5cf6 0%,
    #3b82f6 50%,
    #8a5cf6 100%);
  box-shadow: 0 0 10px rgba(138, 92, 246, 0.8);
  animation: neonFlow 3s ease-in-out infinite;
  background-size: 200% 100%;
}

@keyframes neonFlow {
  0%, 100% {
    background-position: 0% 50%;
    box-shadow:
      0 0 10px rgba(138, 92, 246, 0.8),
      0 0 20px rgba(138, 92, 246, 0.5);
  }
  50% {
    background-position: 100% 50%;
    box-shadow:
      0 0 15px rgba(59, 130, 246, 0.9),
      0 0 30px rgba(59, 130, 246, 0.6);
  }
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 40px;
  gap: 20px;
}

/* 中央导航项 */
.nav-items {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
}

/* 语言切换器 */
.language-switcher {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.lang-btn {
  border-color: rgba(236, 72, 153, 0.5);
  background: linear-gradient(135deg, rgba(138, 92, 246, 0.2), rgba(236, 72, 153, 0.2));
}

.lang-btn:hover {
  border-color: rgba(236, 72, 153, 0.8);
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.4);
}

/* 赛博朋克按钮 */
.cyber-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(26, 26, 46, 0.6);
  border: 1px solid rgba(138, 92, 246, 0.3);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  white-space: nowrap;
}

.cyber-btn:hover {
  border-color: rgba(138, 92, 246, 0.8);
  background: rgba(138, 92, 246, 0.15);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(138, 92, 246, 0.4);
}

.cyber-btn:active {
  transform: translateY(0);
}

.cyber-btn.primary {
  border-color: rgba(59, 130, 246, 0.5);
}

.cyber-btn.primary:hover {
  border-color: rgba(59, 130, 246, 0.8);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.cyber-btn.admin {
  border-color: rgba(236, 72, 153, 0.5);
  background: linear-gradient(135deg, rgba(138, 92, 246, 0.2), rgba(236, 72, 153, 0.2));
}

.cyber-btn.admin:hover {
  border-color: rgba(236, 72, 153, 0.8);
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.4);
}

.btn-icon {
  position: relative;
  z-index: 2;
  font-size: 14px;
  line-height: 1;
}

.attention-btn::after {
  content: '';
  position: absolute;
  top: -70%;
  left: -30%;
  z-index: 1;
  width: 22%;
  height: 240%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.08) 35%,
    rgba(138, 92, 246, 0.42) 50%,
    rgba(255, 255, 255, 0.08) 65%,
    transparent 100%
  );
  transform: translateX(-260%) rotate(16deg);
  animation: attentionSweep 2.8s ease-in-out infinite;
  pointer-events: none;
}

.cyber-btn.attention-btn {
  border-color: rgba(59, 130, 246, 0.82);
  background: linear-gradient(110deg, rgba(59, 130, 246, 0.2), rgba(138, 92, 246, 0.2));
  animation: attentionButtonPulse 1.55s ease-in-out infinite;
}

.attention-btn::before {
  content: '';
  position: absolute;
  inset: -1px;
  z-index: 0;
  border: 2px solid rgba(96, 165, 250, 0.9);
  border-radius: 5px;
  opacity: 0;
  transform: scale(0.98);
  animation: attentionRingPulse 1.55s ease-out infinite;
  pointer-events: none;
}

.attention-btn .btn-icon {
  animation: attentionIconSpin 2.6s linear infinite;
}

.attention-badge {
  position: relative;
  z-index: 2;
  margin-left: 4px;
  padding: 2px 5px;
  border-radius: 999px;
  color: #111827;
  background: #fbbf24;
  box-shadow: 0 0 12px rgba(251, 191, 36, 0.8);
  font-size: 8px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0.06em;
  animation: attentionBadgePulse 1.1s ease-in-out infinite;
}

@keyframes attentionButtonPulse {
  0%, 100% {
    box-shadow:
      0 0 10px rgba(59, 130, 246, 0.45),
      inset 0 0 8px rgba(138, 92, 246, 0.12);
  }
  50% {
    border-color: rgba(251, 191, 36, 0.9);
    box-shadow:
      0 0 20px rgba(59, 130, 246, 0.8),
      0 0 34px rgba(138, 92, 246, 0.48),
      inset 0 0 14px rgba(251, 191, 36, 0.14);
  }
}

@keyframes attentionRingPulse {
  0% {
    opacity: 0.75;
    transform: scale(0.98);
  }
  70%, 100% {
    opacity: 0;
    transform: scale(1.08, 1.28);
  }
}

@keyframes attentionBadgePulse {
  0%, 100% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(1.14);
    filter: brightness(1.2);
  }
}

@keyframes attentionSweep {
  0%, 18% {
    transform: translateX(-260%) rotate(16deg);
    opacity: 0;
  }
  28% {
    opacity: 1;
  }
  62%, 100% {
    transform: translateX(680%) rotate(16deg);
    opacity: 0;
  }
}

@keyframes attentionIconSpin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.btn-text {
  position: relative;
  z-index: 2;
  transition: text-shadow 0.3s;
}

.cyber-btn:hover .btn-text {
  text-shadow:
    0 0 8px rgba(138, 92, 246, 0.8),
    0 0 16px rgba(138, 92, 246, 0.5);
  animation: textPulse 1s ease-in-out infinite;
}

@keyframes textPulse {
  0%, 100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.2);
  }
}

.btn-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(138, 92, 246, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  pointer-events: none;
}

.cyber-btn:hover .btn-glow {
  width: 200px;
  height: 200px;
}

/* 活动状态 */
.cyber-btn.active {
  background: rgba(138, 92, 246, 0.25);
  border-color: rgba(138, 92, 246, 0.8);
  box-shadow: 0 0 20px rgba(138, 92, 246, 0.6);
  animation: activePulse 2s ease-in-out infinite;
}

@keyframes activePulse {
  0%, 100% {
    box-shadow:
      0 0 20px rgba(138, 92, 246, 0.6),
      inset 0 0 10px rgba(138, 92, 246, 0.2);
  }
  50% {
    box-shadow:
      0 0 30px rgba(138, 92, 246, 0.8),
      0 0 40px rgba(138, 92, 246, 0.4),
      inset 0 0 15px rgba(138, 92, 246, 0.3);
  }
}

.cyber-btn.active .btn-text {
  text-shadow:
    0 0 10px rgba(138, 92, 246, 1),
    0 0 20px rgba(138, 92, 246, 0.6);
  animation: activeTextGlow 2s ease-in-out infinite;
}

@keyframes activeTextGlow {
  0%, 100% {
    filter: brightness(1.2);
  }
  50% {
    filter: brightness(1.5);
  }
}

/* 下拉菜单样式 */
.cyber-dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-toggle {
  position: relative;
  padding-right: 35px !important;
}

.lock-indicator {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  pointer-events: none;
  animation: lockPulse 2s ease-in-out infinite;
  z-index: 10;
}

@keyframes lockPulse {
  0%, 100% {
    opacity: 0.7;
    transform: translateY(-50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translateY(-50%) scale(1.2);
  }
}

.dropdown-arrow {
  position: absolute;
  right: 22px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  transition: transform 0.3s ease, color 0.3s ease;
  pointer-events: none;
}

.cyber-dropdown.active .dropdown-arrow {
  transform: translateY(-50%) rotate(180deg);
  color: rgba(138, 92, 246, 1);
}

/* 锁定状态的视觉指示 - 更强的发光效果 */
.cyber-dropdown.active .dropdown-toggle {
  border-color: rgba(138, 92, 246, 0.8);
  background: rgba(138, 92, 246, 0.25);
  box-shadow:
    0 0 20px rgba(138, 92, 246, 0.6),
    0 0 40px rgba(138, 92, 246, 0.3),
    inset 0 0 10px rgba(138, 92, 246, 0.2);
  animation: dropdownLocked 2s ease-in-out infinite;
}

.cyber-dropdown.active.primary .dropdown-toggle {
  border-color: rgba(59, 130, 246, 0.8);
  background: rgba(59, 130, 246, 0.25);
  box-shadow:
    0 0 20px rgba(59, 130, 246, 0.6),
    0 0 40px rgba(59, 130, 246, 0.3),
    inset 0 0 10px rgba(59, 130, 246, 0.2);
  animation: dropdownLockedPrimary 2s ease-in-out infinite;
}

@keyframes dropdownLocked {
  0%, 100% {
    box-shadow:
      0 0 20px rgba(138, 92, 246, 0.6),
      0 0 40px rgba(138, 92, 246, 0.3),
      inset 0 0 10px rgba(138, 92, 246, 0.2);
  }
  50% {
    box-shadow:
      0 0 30px rgba(138, 92, 246, 0.8),
      0 0 60px rgba(138, 92, 246, 0.4),
      inset 0 0 15px rgba(138, 92, 246, 0.3);
  }
}

@keyframes dropdownLockedPrimary {
  0%, 100% {
    box-shadow:
      0 0 20px rgba(59, 130, 246, 0.6),
      0 0 40px rgba(59, 130, 246, 0.3),
      inset 0 0 10px rgba(59, 130, 246, 0.2);
  }
  50% {
    box-shadow:
      0 0 30px rgba(59, 130, 246, 0.8),
      0 0 60px rgba(59, 130, 246, 0.4),
      inset 0 0 15px rgba(59, 130, 246, 0.3);
  }
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  background: rgba(10, 14, 39, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(138, 92, 246, 0.4);
  border-radius: 8px;
  box-shadow:
    0 8px 32px rgba(138, 92, 246, 0.3),
    0 0 20px rgba(138, 92, 246, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  overflow: hidden;
  margin-top: 5px;
}

.cyber-dropdown.active .dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

/* 下拉菜单背景动画 */
.dropdown-menu::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #8a5cf6, #3b82f6, #8a5cf6);
  background-size: 200% 100%;
  animation: dropdownGlow 3s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(138, 92, 246, 0.8);
}

@keyframes dropdownGlow {
  0%, 100% {
    background-position: 0% 50%;
    box-shadow: 0 0 10px rgba(138, 92, 246, 0.8);
  }
  50% {
    background-position: 100% 50%;
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.9);
  }
}

.dropdown-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(138, 92, 246, 0.1);
  position: relative;
  overflow: hidden;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: rgba(138, 92, 246, 0.2);
  color: rgba(255, 255, 255, 1);
  text-shadow: 0 0 8px rgba(138, 92, 246, 0.8);
  transform: translateX(4px);
}

.dropdown-item.active {
  background: rgba(138, 92, 246, 0.25);
  color: rgba(138, 92, 246, 1);
  text-shadow: 0 0 10px rgba(138, 92, 246, 1);
  border-left: 3px solid rgba(138, 92, 246, 1);
}

/* 暂未开放的入口：置灰，但保持可点击 —— 点了给「功能暂未开放」提示，
   比 disabled 属性（点了完全没反应）体验更好 */
.dropdown-item.is-disabled {
  color: rgba(255, 255, 255, 0.32);
  cursor: not-allowed;
}

.dropdown-item.is-disabled .item-icon {
  opacity: 0.45;
  filter: none;
}

.dropdown-item.is-disabled:hover {
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.45);
  text-shadow: none;
  transform: none;
}

.dropdown-item.is-disabled:hover::before {
  width: 0;
}

.item-lock {
  flex-shrink: 0;
  font-size: 12px;
  opacity: 0.5;
}

.study-menu .dropdown-item.is-disabled:hover {
  background: rgba(255, 255, 255, 0.04);
}

.dropdown-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: linear-gradient(90deg, rgba(138, 92, 246, 0.3), transparent);
  transition: width 0.3s ease;
}

.dropdown-item:hover::before {
  width: 100%;
}

.item-icon {
  font-size: 16px;
  flex-shrink: 0;
  filter: drop-shadow(0 0 2px rgba(138, 92, 246, 0.5));
  transition: all 0.3s ease;
}

.dropdown-item:hover .item-icon {
  filter: drop-shadow(0 0 6px rgba(138, 92, 246, 0.9));
  transform: scale(1.1);
}

.item-text {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 为不同类型的下拉菜单添加特定样式 */
.tools-menu .dropdown-item:hover {
  background: rgba(138, 92, 246, 0.2);
}

.study-menu .dropdown-item:hover {
  background: rgba(59, 130, 246, 0.2);
}

.study-menu .dropdown-item.active {
  background: rgba(59, 130, 246, 0.25);
  color: rgba(59, 130, 246, 1);
  text-shadow: 0 0 10px rgba(59, 130, 246, 1);
  border-left: 3px solid rgba(59, 130, 246, 1);
}

.study-menu::before {
  background: linear-gradient(90deg, #3b82f6, #8a5cf6, #3b82f6);
  background-size: 200% 100%;
  animation: dropdownGlow 3s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.8);
}

/* 响应式 */
@media (max-width: 768px) {
  .nav-content {
    padding: 0 20px;
  }

  .btn-text {
    display: none;
  }

  .cyber-btn {
    padding: 8px 12px;
  }

  .nav-items {
    gap: 8px;
  }

  /* 移动端下拉菜单样式 */
  .dropdown-menu {
    min-width: 160px;
    left: auto;
    right: 0;
    transform: translateX(0) translateY(-10px);
  }

  .cyber-dropdown.active .dropdown-menu {
    transform: translateX(0) translateY(0);
  }

  .dropdown-item {
    padding: 10px 12px;
    font-size: 13px;
  }

  .item-text {
    display: none;
  }

  .dropdown-item {
    justify-content: center;
    padding: 8px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .nav-items {
    gap: 6px;
  }

  .cyber-btn {
    padding: 6px 8px;
  }

  .btn-icon {
    font-size: 12px;
  }

  .dropdown-menu {
    min-width: 140px;
  }
}

</style>
